import { type FC, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useSupabaseCallback } from "../../../../../server/supabase/hooks";
import { useToken } from "../../../../../server/auth/use-token";
import { formatDatetime } from "../../../../../utils/iso8601";
import { useDialog } from "../../../../../utils/hooks/use-dialog";
import { serviceURL } from "../../../../../utils/service-url";
import type { UUID } from "../../../../../utils/uuid";
import { SupabaseMomentService } from "../../../../moment/server/supabase-moment-service";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { publishMomentToShowroom } from "./publish-moment-to-showroom";
import { useEmailCommunicationMoments } from "../hooks/use-email-communication-moments";
import { getEffectiveDate, type UseMomentProperties } from "../../../../moment-card/types";

export const EmailCommunicationLink: FC<{
  onUseAsLink: (url: string, title?: string) => void;
}> = ({ onUseAsLink }) => {
  const emailCommunicationsDialog = useDialog();
  const [selected, setSelected] = useState<UUID | null>(null);
  const [selectedMoment, setSelectedMoment] = useState<{ id: UUID; title: string } | null>(null);
  const [searchText, setSearchText] = useState("");
  const [selecting, setSelecting] = useState(false);
  const getToken = useToken();

  const { data: moments, loading, error } = useEmailCommunicationMoments();

  const handleSelect = useSupabaseCallback(
    async ({ supabase, account_id }) => {
      if (!selected) return;

      setSelecting(true);
      try {
        const { data, error: fetchError } = await new SupabaseMomentService(supabase).get(selected);

        if (fetchError || !data) {
          toast.error("Failed to load the selected communication");
          return;
        }

        let showroomUrl: string;

        if (data.showroom_key) {
          showroomUrl = `${serviceURL("showroom")}${data.showroom_key}`;
        } else {
          const token = await getToken();
          const url = await publishMomentToShowroom(supabase, account_id, selected, token);

          if (!url) {
            toast.error("Failed to generate a sharing link for this communication");
            return;
          }

          showroomUrl = url;
        }

        setSelectedMoment({ id: selected, title: data.title });
        onUseAsLink(showroomUrl, data.title);
        emailCommunicationsDialog.handleClose();
      } catch {
        toast.error("Failed to generate a sharing link for this communication");
      } finally {
        setSelecting(false);
      }
    },
    [selected, onUseAsLink, emailCommunicationsDialog, getToken],
  );

  const filteredMoments = useMemo(() => {
    if (!moments) return [];
    if (!searchText.trim()) return moments;
    const lower = searchText.trim().toLowerCase();
    return moments.filter(
      (m) =>
        m.title?.toLowerCase().includes(lower) ||
        m.program?.title?.toLowerCase().includes(lower) ||
        m.segment?.name?.toLowerCase().includes(lower),
    );
  }, [moments, searchText]);

  const formatScheduleDate = (moment: UseMomentProperties): string => {
    const when = getEffectiveDate(moment);
    if (!when) return "—";
    const timezoneLabel = moment.timezone ? ` (${moment.timezone})` : "";
    return formatDatetime(new Date(when)) + timezoneLabel;
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info" icon={false}>
        <Typography variant="body2" fontSize="14px">
          Insert a shareable link to email Communications. Does not work for Slack, Teams, or SMS
          Communications.
        </Typography>
        <Typography variant="body2" fontSize="14px">
          <ul style={{ paddingLeft: "1em" }}>
            <li>
              Merge tags will be replaced with default values, which will not be personalized to
              recipients
            </li>
            <li>Dynamic content blocks will be hidden</li>
            <li>
              <b>Communication will be viewable by anyone with the link</b>, without needing to log
              in.
            </li>
          </ul>
        </Typography>
      </Alert>
      {selectedMoment ? (
        <Stack spacing={0.5}>
          <Typography variant="body2" fontWeight={500} fontSize="14px">
            Email Communication
          </Typography>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2" fontSize="14px">
              {selectedMoment.title}
            </Typography>
            <Stack direction="row" flexShrink={0}>
              <Button
                data-analytics-id="button-form-view-email-communication"
                href={`/moments/${selectedMoment.id}`}
                target="_blank"
                size="small"
              >
                View
              </Button>
              <Button
                data-analytics-id="button-form-change-email-communication"
                onClick={emailCommunicationsDialog.handleOpen}
                size="small"
              >
                Change
              </Button>
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <Stack alignItems="center">
          <Button
            data-analytics-id="button-form-select-email-communication"
            onClick={emailCommunicationsDialog.handleOpen}
            sx={{ width: "auto" }}
          >
            Select Email Communication
          </Button>
        </Stack>
      )}
      <Dialog
        open={emailCommunicationsDialog.open}
        onClose={emailCommunicationsDialog.handleClose}
        fullWidth={true}
        maxWidth="lg"
        slotProps={{ paper: { sx: { maxHeight: 900 } } }}
      >
        <DialogTitle sx={{ p: 3 }}>Select an Email Communication</DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="body2" fontSize="14px">
              You can only link to published email Communications - not Slack, Teams, or SMS
              Communications
            </Typography>
            <TextField
              label="Search Communications"
              variant="outlined"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {loading && (
              <Stack alignItems="center" py={4}>
                <CircularProgress />
              </Stack>
            )}
            {error && (
              <Alert severity="error">Failed to load email communications. Please try again.</Alert>
            )}
            {!loading && !error && (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    {["Communication", "Program", "Send Date", "Segment"].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          py: 1.5,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredMoments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ borderBottom: "none", py: 3 }}>
                        <Typography variant="body2" textAlign="center" color="text.secondary">
                          {searchText.trim()
                            ? "No communications match your search"
                            : "No published email communications found"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredMoments.map((moment) => (
                    <TableRow key={moment.id}>
                      <TableCell sx={{ borderBottom: "none", py: 1 }}>
                        <Radio
                          checked={selected === moment.id}
                          onChange={() => setSelected(moment.id)}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          borderBottom: "none",
                          py: 1,
                          width: 300,
                          maxWidth: 300,
                        }}
                      >
                        <Link
                          href={`/moments/${moment.id || ""}`}
                          target="_blank"
                          rel="noopener"
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            fontSize: "14px",
                            fontWeight: 400,
                          }}
                        >
                          {moment.title}
                        </Link>
                      </TableCell>
                      <TableCell sx={{ borderBottom: "none", fontSize: "14px", fontWeight: 400 }}>
                        {moment.program?.title ?? "—"}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "none", fontSize: "14px", fontWeight: 400 }}>
                        {formatScheduleDate(moment)}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "none", fontSize: "14px", fontWeight: 400 }}>
                        {moment.segment?.name ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            data-analytics-id="email-communication-dialog-cancel"
            type="button"
            onClick={emailCommunicationsDialog.handleClose}
            loading={false}
          >
            Cancel
          </Button>
          <Button
            data-analytics-id="email-communication-dialog-select"
            type="button"
            variant="contained"
            disabled={selected === null}
            loading={selecting}
            onClick={() => void handleSelect()}
          >
            Select
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
